import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DemoWidget from '../components/DemoWidget';
import { WORKERS } from '../workerConfig';
import { fetchJson } from '../api';

// ── LLM Chat ───────────────────────────────────────────────────────
interface Message { role: 'user' | 'assistant'; content: string; tokens?: number; ms?: number; cached?: boolean; }

function LLMChatDemo() {
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: "Hi! I'm running on Workers AI (Llama 3.1 8B Instruct) with an application-level KV cache. Ask me about any Cloudflare product." }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg: Message = { role: 'user', content: input };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setLoading(true);
    setError(null);
    try {
      const data = await fetchJson<{ response: string; cached?: boolean; latencyMs?: number; tokens?: number }>(`${WORKERS.aiDemo}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [...messages, userMsg].map(({ role, content }) => ({ role, content })),
          system: 'You are a concise Cloudflare demo assistant. Keep answers to 2-3 sentences max. Focus on Cloudflare products and their value.',
        }),
      });
      const content = typeof data.response === 'string' ? data.response : JSON.stringify(data.response);
      setMessages((m) => [...m, { 
        role: 'assistant', 
        content, 
        ms: data.latencyMs, 
        tokens: data.tokens ?? Math.round(content.split(' ').length * 1.3),
        cached: data.cached 
      }]);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Workers AI unreachable');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {[
          { label: 'Model', value: 'llama-3.1-8b-instruct' },
          { label: 'Provider', value: 'Workers AI' },
          { label: 'Messages', value: String(messages.length) },
        ].map((m) => (
          <div key={m.label} style={{ flex: 1, minWidth: '120px', background: 'var(--cf-bg)', borderRadius: 'var(--radius-sm)', padding: '0.5rem 0.75rem', border: '1px solid var(--cf-border)' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--cf-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.label}</div>
            <div style={{ fontSize: '0.85rem', fontWeight: 600, fontFamily: 'monospace', color: 'var(--cf-dark)', marginTop: '0.15rem' }}>{m.value}</div>
          </div>
        ))}
      </div>

      <div style={{ background: 'var(--cf-bg)', border: '1px solid var(--cf-border)', borderRadius: 'var(--radius-sm)', height: '260px', overflowY: 'auto', padding: '0.875rem', marginBottom: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start', gap: '0.25rem' }}>
            <div style={{
              maxWidth: '80%', padding: '0.625rem 0.875rem', borderRadius: '12px',
              background: m.role === 'user' ? 'var(--cf-orange)' : 'white',
              color: m.role === 'user' ? 'white' : 'var(--cf-dark)',
              fontSize: '0.875rem', lineHeight: '1.5',
              border: m.role === 'assistant' ? '1px solid var(--cf-border)' : 'none',
              boxShadow: m.role === 'assistant' ? 'var(--shadow)' : 'none',
            }}>
              {m.content}
            </div>
            {m.role === 'assistant' && m.tokens && (
              <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.68rem', color: 'var(--cf-muted)', alignItems: 'center' }}>
                <span>{m.tokens} tokens</span>
                <span>·</span>
                <span>{m.ms}ms</span>
                {m.cached && (
                  <span style={{ 
                    background: 'var(--cf-success)', 
                    color: 'white', 
                    padding: '0.1rem 0.4rem', 
                    borderRadius: '4px', 
                    fontWeight: 700,
                    fontSize: '0.6rem',
                    textTransform: 'uppercase'
                  }}>
                    CACHE HIT
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--cf-muted)', fontSize: '0.85rem' }}>
            <span className="spinner" style={{ width: '14px', height: '14px' }} /> Generating (Workers AI)...
          </div>
        )}
        {error && (
          <div style={{ fontSize: '0.8rem', color: 'var(--cf-danger)', padding: '0.5rem' }}>{error}</div>
        )}
        <div ref={bottomRef} />
      </div>

      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <input
          className="form-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Ask about Workers, D1, R2, AI Gateway..."
          disabled={loading}
          style={{ flex: 1 }}
        />
        <button className="btn btn-primary" onClick={send} disabled={loading || !input.trim()}>
          Send
        </button>
      </div>
    </div>
  );
}

// ── Application Cache Metrics ──────────────────────────────────────
function AIGatewayDemo() {
  const [running, setRunning] = useState(false);
  const [metrics, setMetrics] = useState<null | { 
    totalRequests: number; 
    cacheHits: number; 
    cacheMisses: number; 
    avgLatencyMs: number; 
    cachedTokens: number; 
    estimatedSavings: string;
    cacheHitRate: string;
    modelBreakdown: Record<string, number>;
  }>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStats = async () => {
    setRunning(true);
    setMetrics(null);
    setError(null);
    try {
      const data = await fetchJson<{ metrics: typeof metrics }>(`${WORKERS.aiDemo}/gateway/stats`);
      if (data.metrics) {
        setMetrics(data.metrics);
      } else {
        setError('No metrics data returned');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch cache stats');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        This demo tracks real Workers AI requests and an application-managed KV cache. It is not AI Gateway analytics.
      </p>

      <button className="btn btn-primary" onClick={loadStats} disabled={running} style={{ marginBottom: '1.25rem' }}>
        {running ? <><span className="spinner" /> Loading stats...</> : 'Load Today\'s Analytics'}
      </button>

      {error && (
        <div className="result-box result-error" style={{ fontSize: '0.875rem', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {metrics && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            {[
              { label: 'Total Requests', value: String(metrics.totalRequests), color: 'var(--cf-dark)' },
              { label: 'Cache Hits', value: `${metrics.cacheHits} (${metrics.cacheHitRate}%)`, color: 'var(--cf-success)' },
              { label: 'Avg Latency', value: `${metrics.avgLatencyMs}ms`, color: 'var(--cf-orange)' },
            ].map((s) => (
              <div key={s.label} style={{ background: 'var(--cf-bg)', border: '1px solid var(--cf-border)', borderRadius: 'var(--radius-sm)', padding: '0.875rem', textAlign: 'center' }}>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--cf-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>{s.label}</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          {metrics.totalRequests > 0 ? (
            <div className="result-box result-success" style={{ fontSize: '0.875rem' }}>
              <strong>{metrics.cachedTokens.toLocaleString()} tokens served from cache</strong> — estimated savings: <strong>{metrics.estimatedSavings}</strong> today.
              <br /><br />
              {metrics.cacheHits > 0 
                ? `Cached responses returned in ~5ms instead of 500-2000ms. That's ${metrics.cacheHits} requests that didn't need GPU inference.`
                : 'No cache hits yet. Cache keys include the conversation context, so only identical request payloads produce a hit.'}
            </div>
          ) : (
            <div className="result-box" style={{ fontSize: '0.875rem', background: 'var(--cf-bg)' }}>
              No requests today yet. Use the LLM Chat above to generate some traffic, then refresh stats.
            </div>
          )}

          {Object.keys(metrics.modelBreakdown).length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--cf-muted)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Requests by Model</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {Object.entries(metrics.modelBreakdown).map(([model, count]) => (
                  <span key={model} style={{ 
                    fontSize: '0.75rem', 
                    padding: '0.25rem 0.5rem', 
                    background: 'var(--cf-bg)', 
                    border: '1px solid var(--cf-border)', 
                    borderRadius: '4px',
                    fontFamily: 'monospace'
                  }}>
                    {model.split('/').pop()}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Search Prototype ───────────────────────────────────────────────
const KNOWLEDGE_BASE = [
  { id: 1, title: 'Workers Pricing', content: 'Workers are free for 100,000 requests/day. Paid plans start at $5/month for 10 million requests.' },
  { id: 2, title: 'R2 vs S3', content: 'R2 has zero egress fees. S3 charges $0.09/GB outbound. For 1TB/month, R2 saves $90 vs S3.' },
  { id: 3, title: 'D1 Limits', content: 'D1 supports databases up to 10GB. Max query size 100KB. Up to 1000 databases per account.' },
  { id: 4, title: 'AI Gateway Features', content: 'AI Gateway provides caching, rate limiting, logging, and fallback for OpenAI, Anthropic, Hugging Face, and Workers AI.' },
  { id: 5, title: 'Turnstile vs reCAPTCHA', content: 'Turnstile never shows visual puzzles. It uses browser signals and machine learning to verify humans invisibly.' },
];

function RAGDemo() {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<typeof KNOWLEDGE_BASE>([]);

  const search = () => {
    if (!query.trim()) return;
    setSearching(true);
    setResults([]);
    setTimeout(() => {
      const lower = query.toLowerCase();
      const scored = KNOWLEDGE_BASE.map((doc) => ({
        ...doc,
        score: (doc.title.toLowerCase().includes(lower) ? 2 : 0) +
               (doc.content.toLowerCase().split(' ').filter((w) => lower.includes(w) || (w.length > 4 && lower.includes(w.slice(0, -1)))).length * 0.5),
      })).sort((a, b) => b.score - a.score).filter((d) => d.score > 0 || query.length < 4).slice(0, 3);
      setResults(scored.length ? scored : KNOWLEDGE_BASE.slice(0, 2));
      setSearching(false);
    }, 800);
  };

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        This prototype searches a small in-browser knowledge base using keyword matching. It previews the UI flow
        for a future Workers AI and Vectorize implementation.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        {['pricing', 'egress fees', 'AI Gateway'].map((s) => (
          <button key={s} className="btn btn-secondary btn-sm" onClick={() => setQuery(s)}>{s}</button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
        <input
          className="form-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && search()}
          placeholder="Search the knowledge base..."
          style={{ flex: 1 }}
        />
        <button className="btn btn-primary" onClick={search} disabled={searching || !query.trim()}>
          {searching ? <span className="spinner" /> : 'Search'}
        </button>
      </div>

      {results.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {results.map((r, i) => (
            <div key={r.id} style={{ background: 'var(--cf-bg)', border: '1px solid var(--cf-border)', borderRadius: 'var(--radius-sm)', padding: '0.875rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                <strong style={{ fontSize: '0.875rem' }}>{r.title}</strong>
                <span style={{ fontSize: '0.72rem', color: 'var(--cf-muted)', fontFamily: 'monospace' }}>
                  rank: {i + 1}
                </span>
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--cf-mid)', lineHeight: '1.5' }}>{r.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sentiment Analysis ─────────────────────────────────────────────
function SentimentDemo() {
  const [text, setText] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<null | { label: string; score: number; model: string; ms: number }>(null);
  const [error, setError] = useState<string | null>(null);

  const analyze = async () => {
    setAnalyzing(true);
    setResult(null);
    setError(null);
    const start = Date.now();
    try {
      const data = await fetchJson<{ result: Array<{ label: string; score: number }> }>(`${WORKERS.aiDemo}/classify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      const ms = Date.now() - start;
      const top = Array.isArray(data.result) ? data.result.sort((a, b) => b.score - a.score)[0] : null;
      if (top) {
        setResult({ label: top.label, score: top.score, model: '@cf/huggingface/distilbert-sst-2-int8', ms });
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Workers AI classification failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const labelColor = result?.label === 'POSITIVE' ? 'var(--cf-success)' : result?.label === 'NEGATIVE' ? 'var(--cf-danger)' : 'var(--cf-warning)';

  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--cf-muted)', marginBottom: '1rem' }}>
        Workers AI runs inference at the edge using DistilBERT. Paste any text — product reviews, support tickets,
        social mentions — and get a classification with confidence score.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        {[
          'Cloudflare is amazing, best CDN I have ever used!',
          'The site is slow and keeps breaking on mobile.',
          'We are evaluating different CDN options for our team.',
        ].map((sample, i) => (
          <button key={i} className="btn btn-secondary btn-sm" onClick={() => setText(sample)}>
            Sample {i + 1}
          </button>
        ))}
      </div>

      <textarea
        className="form-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste any text to classify..."
        rows={3}
        style={{ marginBottom: '0.75rem' }}
      />

      <button className="btn btn-primary" onClick={analyze} disabled={analyzing || !text.trim()}>
        {analyzing ? <><span className="spinner" /> Analyzing...</> : 'Run Inference'}
      </button>

      {error && <div className="result-box result-error" style={{ marginTop: '1rem' }}>{error}</div>}

      {result && (
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '180px', background: `${labelColor}18`, border: `1px solid ${labelColor}44`, borderRadius: 'var(--radius-sm)', padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--cf-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.25rem' }}>Sentiment</div>
            <div style={{ fontSize: '1.75rem', fontWeight: 900, color: labelColor }}>{result.label}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--cf-muted)', marginTop: '0.2rem' }}>
              {(result.score * 100).toFixed(1)}% confidence
            </div>
          </div>
          <div style={{ flex: '1', minWidth: '180px' }}>
            <div className="metric-row">
              <span className="metric-label">Model</span>
              <code style={{ fontSize: '0.75rem' }}>distilbert-sst-2</code>
            </div>
            <div className="metric-row">
              <span className="metric-label">Inference time</span>
              <span className="metric-value">{result.ms}ms</span>
            </div>
            <div className="metric-row">
              <span className="metric-label">Platform</span>
              <span className="metric-value">Workers AI</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────
export default function AIPage() {
  return (
    <main className="main">
      <div className="demo-page">
        <div className="demo-page-header">
          <div className="breadcrumb">
            <Link to="/">Portal</Link>
            <span className="breadcrumb-sep">/</span>
            <span>AI Platform</span>
          </div>
          <h2>AI Platform Demos</h2>
          <p>
            Live Workers AI inference plus clearly labeled application-cache and search prototypes.
          </p>
        </div>

        <div className="demo-list">
          <DemoWidget
            icon="💬"
            title="LLM Chat — Workers AI + KV Cache"
            product="Workers AI / KV"
            script="Chat with the live Workers AI model. Cache hits come from an application-level KV cache keyed by the complete conversation payload."
          >
            <LLMChatDemo />
          </DemoWidget>

          <DemoWidget
            icon="📊"
            title="Application Cache Analytics"
            product="Workers AI / KV"
            script="Load today's application metrics. These counters are maintained in KV and are not AI Gateway analytics."
          >
            <AIGatewayDemo />
          </DemoWidget>

          <DemoWidget
            icon="🔍"
            title="Knowledge Search Prototype"
            product="UI Prototype"
            script="Search the sample knowledge base. This currently uses local keyword matching and previews a future Vectorize-backed flow."
          >
            <RAGDemo />
          </DemoWidget>

          <DemoWidget
            icon="😊"
            title="Sentiment Analysis"
            product="Workers AI (DistilBERT)"
            script="Paste a review or support ticket. Run inference. Point at the latency — 15-40ms from the edge. This replaces an API call to a third-party NLP service. No data leaves Cloudflare."
          >
            <SentimentDemo />
          </DemoWidget>
        </div>
      </div>
    </main>
  );
}
